import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { cloneDeep } from 'lodash';
import { TranslateService } from '@ngx-translate/core';

import { FILE_ICON } from '../../../shared/constants';
import { getDocumentUrl } from '../../../shared/helpers';
import { AccountingSettingsModel } from '../../../accounting/accounting-settings-module/models';
import { AccountingSettingsService } from '../../../accounting/accounting-settings-module/services/accounting-settings.service';
import {
  DocumentNumberSettingsComponent,
  ErrorMessage
} from '../../../shared/forms/document-number-settings/document-number-settings.component';
import { DocumentTypesUppercaseEnum } from 'common/src/modules/modals/modals-common/link-document-modal/enums/ducument-types.enum';
import { CommonModalsActionsEnum, WarningModalComponent } from 'common/src/modules/modals/modals-common';
import { ToasterService } from 'common/src/modules/ui-components/toaster';
import {
  OutgoingInvoiceFileValidationModel,
  OutgoingInvoiceImportBatchModel,
  OutgoingInvoiceImportDocumentModel
} from '../../models';
import { InvoiceApiService } from '../../services/invoice-api.service';

@Component({
  selector: 'rnpl-create-oin-import-modal',
  templateUrl: './create-oin-import-modal.component.html',
  styleUrls: ['./create-oin-import-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateOinImportModalComponent implements OnDestroy {
  public selectedFiles: File[] = [];
  public fileValidations: OutgoingInvoiceFileValidationModel[] = [];
  public invoiceNumberSettingsEnabled: boolean = false;
  public serverErrors: ErrorMessage[] = [];
  public readonly fileIcon = FILE_ICON;
  public readonly maxFileSize: number = 10000000;
  public readonly documentType = DocumentTypesUppercaseEnum.OIN;
  public submitClicked: boolean = false;
  public duplicateFilesOpen: boolean = true;
  public uniqueFilesOpen: boolean = false;

  @ViewChild('uploadFileInput', {static: true}) public uploadFileInput: ElementRef;
  private numberSettingsComponent: DocumentNumberSettingsComponent;

  @ViewChild('numberSettings', {static: false})
  public set numberSettings(component: DocumentNumberSettingsComponent) {
    this.numberSettingsComponent = component;
    if (component && component.continueExistingNumberSequenceControl.value !== true) {
      component.continueExistingNumberSequenceControl.patchValue(true);
    }
  }

  public get numberSettings(): DocumentNumberSettingsComponent {
    return this.numberSettingsComponent;
  }

  public readonly validating$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly submitRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private readonly destroy$: ReplaySubject<void> = new ReplaySubject<void>(1);
  private validationVersion: number = 0;

  constructor(
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly translateService: TranslateService,
    private readonly toasterService: ToasterService,
    private readonly invoiceApiService: InvoiceApiService,
    private readonly accountingSettingsService: AccountingSettingsService,
    public readonly dialogRef: MatDialogRef<CreateOinImportModalComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: {accountingSettings: AccountingSettingsModel}
  ) {}

  public uploadFiles(filesList: FileList): void {
    if (!filesList || !filesList.length) { return; }

    const newFiles: File[] = Array.prototype.slice.call(filesList);
    this.selectedFiles = [...this.selectedFiles, ...newFiles]
      .filter((file: File, index: number, files: File[]) => {
        return files.findIndex(item => item.name === file.name && item.size === file.size) === index;
      })
      .filter((file: File) => this.isSupportedFile(file));

    this.uploadFileInput.nativeElement.value = null;
    this.submitClicked = false;
    this.validateFiles();
  }

  public removeFile(file: File): void {
    this.selectedFiles = this.selectedFiles.filter(item => item !== file);
    this.submitClicked = false;
    this.validateFiles();
  }

  public submit(): void {
    this.submitClicked = true;
    if (this.submitRequest$.getValue() || this.validating$.getValue() || !this.validFiles.length) { return; }

    if (!this.invoiceNumberSettingsEnabled) {
      this.continueSubmission();
      return;
    }

    if (!this.numberSettings || !this.numberSettings.isValid()) { return; }

    const accountingSettings: AccountingSettingsModel = cloneDeep(this.data.accountingSettings);
    const settingsIndex: number = accountingSettings.documentIdSettings
      .findIndex(item => item.documentType === DocumentTypesUppercaseEnum.OIN);
    if (settingsIndex < 0) { return; }

    accountingSettings.documentIdSettings[settingsIndex] = this.numberSettings.getUpdated();
    this.submitRequest$.next(true);
    this.serverErrors = [];

    this.accountingSettingsService.updateAccountingSettings(accountingSettings)
      .pipe(
        finalize(() => this.submitRequest$.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe(
        (updatedSettings: AccountingSettingsModel) => {
          this.data.accountingSettings = updatedSettings;
          this.submitRequest$.next(false);
          this.cdr.markForCheck();
          this.continueSubmission();
        },
        error => {
          this.serverErrors = error && error.error && error.error.messages ? error.error.messages : [];
          this.cdr.detectChanges();
        }
      );
  }

  public openDocument(document: OutgoingInvoiceImportDocumentModel): void {
    if (!document || !document.documentId) { return; }
    const documentUrl = getDocumentUrl(document.documentType, document.documentId);
    if (!documentUrl) { return; }
    const url = this.router.serializeUrl(this.router.createUrlTree([documentUrl]));
    window.open(url, '_blank');
  }

  public isFilePdf(file: File): boolean {
    return file.type === 'application/pdf';
  }

  public isFileImage(file: File): boolean {
    return file.type === 'image/png' || file.type === 'image/jpg' || file.type === 'image/jpeg';
  }

  public documentsFor(file: File): OutgoingInvoiceImportDocumentModel[] {
    const validation = this.fileValidations.find(item => item.fileName === file.name);
    return validation && validation.documents ? validation.documents : [];
  }

  get duplicateFiles(): File[] {
    return this.selectedFiles.filter(file => !!this.documentsFor(file).length);
  }

  get uniqueFiles(): File[] {
    return this.selectedFiles.filter(file => !this.documentsFor(file).length);
  }

  get validFiles(): File[] {
    return this.selectedFiles.filter((file: File) => file.size <= this.maxFileSize);
  }

  get oinNumberSettings() {
    if (!this.data.accountingSettings) { return null; }
    return this.data.accountingSettings.documentIdSettings
      .find(item => item.documentType === DocumentTypesUppercaseEnum.OIN);
  }

  private validateFiles(): void {
    const validationVersion = ++this.validationVersion;
    if (!this.validFiles.length) {
      this.fileValidations = [];
      this.validating$.next(false);
      return;
    }

    this.validating$.next(true);
    this.invoiceApiService.validateImportFiles(this.validFiles)
      .pipe(
        finalize(() => {
          if (validationVersion === this.validationVersion) {
            this.validating$.next(false);
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((validations: OutgoingInvoiceFileValidationModel[]) => {
        if (validationVersion !== this.validationVersion) { return; }
        this.fileValidations = validations || [];
        this.cdr.detectChanges();
      });
  }

  private continueSubmission(): void {
    if (this.validFiles.some(file => !!this.documentsFor(file).length)) {
      const warningRef = this.dialog.open(WarningModalComponent, {
        data: {
          title: 'OCR.UPLOAD_INVOICES',
          message: 'OCR.UPLOAD_INVOICES_MSG',
          confirmBtnText: 'BUTTON.CONTINUE',
          confirmBtnIcon: 'arrow-right'
        }
      });

      warningRef.afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe(result => {
          if (result === CommonModalsActionsEnum.CONFIRM) {
            this.upload(true);
          }
        });
      return;
    }

    this.upload(false);
  }

  private upload(confirmed: boolean): void {
    if (this.submitRequest$.getValue()) { return; }
    this.submitRequest$.next(true);
    this.toasterService.notifyRequestMessage({
      key: 'uploadingOutgoingInvoices',
      message: 'OCR.UPLOADING_INVOICES'
    });

    this.invoiceApiService.uploadImportFiles(this.validFiles, confirmed)
      .pipe(
        finalize(() => {
          this.submitRequest$.next(false);
          this.toasterService.hideRequestMessage('uploadingOutgoingInvoices');
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((response: OutgoingInvoiceImportBatchModel) => {
        const results = response && response.results ? response.results : [];
        const fullQuantity = results.length;
        const successQuantity = results.filter(item => item.outcome === 'CREATED').length;
        const message = fullQuantity === successQuantity
          ? this.translateService.instant('OCR.MULTIPLE_UPLOAD_FULL_SUCCESS', {fullQuantity})
          : this.translateService.instant('OCR.MULTIPLE_UPLOAD_SUCCESS', {fullQuantity, successQuantity});
        this.toasterService.notify({type: successQuantity ? 'success' : 'error', message});
        this.dialogRef.close(true);
      });
  }

  private isSupportedFile(file: File): boolean {
    return this.isFilePdf(file) || this.isFileImage(file);
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
